import React, { useState } from 'react';
import { CheckCircle2, XCircle, Trophy, RotateCcw, HelpCircle, ArrowRight } from 'lucide-react';

const QUIZ = [
  {
    q: 'Sur autoroute limitée à 130 km/h, à quelle vitesse maximale votre élève conducteur novice doit-il rouler ?',
    opts: ['130 km/h, comme tout le monde', '120 km/h', '110 km/h', '90 km/h'],
    ok: 2,
    why: 'Les limitations abaissées s\'appliquent : 110 au lieu de 130, 100 au lieu de 110, 80 au lieu de 90. Le disque « conduite accompagnée » doit être apposé à l\'arrière.',
    src: 'securite-routiere.gouv.fr',
  },
  {
    q: 'Quel est le taux d\'alcool maximal autorisé pour l\'élève au volant en conduite accompagnée ?',
    opts: ['0,5 g/L comme les autres conducteurs', '0,2 g/L — en pratique : zéro verre', '0,8 g/L', 'Aucun seuil tant que l\'accompagnateur est sobre'],
    ok: 1,
    why: 'Le seuil est de 0,2 g/L de sang (comme pour tout permis probatoire) : dès le premier verre, il est dépassé. C\'est donc zéro alcool au volant, sans exception.',
    src: 'service-public.fr',
  },
  {
    q: 'Que faut-il faire du livret d\'apprentissage pendant la conduite accompagnée ?',
    opts: ['Le laisser à l\'auto-école', 'Le remplir au fil des trajets et l\'avoir à bord avec l\'AFFI', 'Le présenter uniquement le jour du permis', 'Il ne sert plus à rien après les 20 h de formation'],
    ok: 1,
    why: 'Le livret suit l\'élève : on y note les trajets (km, conditions) et il doit être présentable en cas de contrôle, avec l\'AFFI et l\'accord de l\'assureur.',
    src: 'service-public.fr',
  },
  {
    q: 'Quelles sont les conditions minimales AVANT de pouvoir passer l\'examen pratique en AAC ?',
    opts: ['1 000 km et 6 mois de conduite', '3 000 km ET 1 an de conduite accompagnée', '5 000 km et 2 ans', 'Aucun minimum, c\'est au feeling'],
    ok: 1,
    why: 'Les DEUX conditions sont cumulatives : au moins 3 000 km parcourus ET 1 an de conduite accompagnée (examen possible dès 17 ans).',
    src: 'securite-routiere.gouv.fr',
  },
  {
    q: 'Pour être accompagnateur, il faut…',
    opts: ['Simplement être majeur', 'Avoir le permis B depuis 5 ans sans interruption + l\'accord préalable de l\'assureur', 'Avoir le permis depuis 2 ans', 'Être un parent obligatoirement'],
    ok: 1,
    why: 'Permis B depuis au moins 5 ans sans interruption, accord de l\'assureur, pas de condamnation pour certains délits routiers. Pas besoin d\'être un parent : plusieurs accompagnateurs sont possibles.',
    src: 'service-public.fr (formulaire F2826)',
  },
];

export default function AccompagnateurQuiz() {
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const q = QUIZ[idx];
  const isLast = idx === QUIZ.length - 1;

  const pick = (i) => { if (picked === null) { setPicked(i); if (i === q.ok) setScore((s) => s + 1); } };
  const next = () => { if (isLast) setDone(true); else { setIdx(idx + 1); setPicked(null); } };
  const restart = () => { setIdx(0); setPicked(null); setScore(0); setDone(false); };

  return (
    <div className="rounded-2xl border border-emerald-600/35 bg-gradient-to-br from-emerald-950/25 to-[#0d0d0e] p-5 sm:p-6" data-testid="accompagnateur-quiz">
      <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-[0.22em] font-bold mb-1.5 inline-flex items-center gap-1.5">
        <HelpCircle size={12} /> Mini-quiz accompagnateurs
      </div>
      <h3 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">Êtes-vous prêt à accompagner ?</h3>
      <p className="text-white/60 text-[13px] mt-1.5">5 questions essentielles avant de prendre la route ensemble. Réponses issues des sources officielles.</p>

      {!done ? (
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-3">
            {QUIZ.map((_, i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full transition ${i < idx ? 'bg-emerald-500' : i === idx ? 'bg-emerald-500/50' : 'bg-white/10'}`} />
            ))}
          </div>
          <div className="text-white/45 text-[11px] font-mono-spec mb-2">Question {idx + 1} / {QUIZ.length}</div>
          <p className="text-white text-[15px] font-semibold leading-snug">{q.q}</p>
          <div className="mt-3.5 space-y-2">
            {q.opts.map((opt, i) => {
              const revealed = picked !== null;
              const isOk = i === q.ok;
              const isPicked = i === picked;
              let cls = 'border-white/12 bg-white/[0.04] text-white/80 hover:bg-white/10 hover:text-white';
              if (revealed && isOk) cls = 'border-emerald-500 bg-emerald-600/20 text-emerald-100';
              else if (revealed && isPicked) cls = 'border-red-500 bg-red-600/15 text-red-200';
              else if (revealed) cls = 'border-white/8 bg-white/[0.02] text-white/40';
              return (
                <button key={i} onClick={() => pick(i)} disabled={revealed} data-testid={`quiz-q${idx}-opt${i}`}
                  className={`w-full text-left rounded-xl border px-4 py-3 text-[13.5px] leading-snug transition flex items-start gap-2.5 ${cls}`}>
                  {revealed && isOk && <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-emerald-400" />}
                  {revealed && isPicked && !isOk && <XCircle size={16} className="shrink-0 mt-0.5 text-red-400" />}
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>
          {picked !== null && (
            <div className="mt-3.5 rounded-xl border border-sky-500/30 bg-sky-950/20 p-3.5" data-testid="quiz-explanation">
              <p className="text-white/85 text-[12.5px] leading-relaxed">{q.why}</p>
              <p className="text-white/40 text-[10.5px] font-mono-spec mt-1.5">Source : {q.src}</p>
              <button onClick={next} data-testid="quiz-next"
                className="mt-3 h-10 px-5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[13px] font-bold inline-flex items-center gap-1.5">
                {isLast ? 'Voir mon résultat' : 'Question suivante'} <ArrowRight size={13} />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-5 text-center" data-testid="quiz-result">
          <Trophy size={36} className={`mx-auto ${score >= 4 ? 'text-emerald-400' : score >= 3 ? 'text-sky-400' : 'text-amber-400'}`} />
          <div className="mt-2 font-serif-editorial text-white text-[30px]" data-testid="quiz-score">{score} / {QUIZ.length}</div>
          <p className="mt-1.5 text-white/75 text-[14px] max-w-md mx-auto leading-relaxed">
            {score === QUIZ.length && 'Parfait ! Vous maîtrisez les règles essentielles : bonne route avec votre élève.'}
            {score === 4 && 'Très bien ! Un petit point à revoir dans le guide ci-dessus et vous êtes prêt.'}
            {score === 3 && 'Pas mal ! Relisez le guide de l\'accompagnateur ci-dessus pour consolider les règles.'}
            {score < 3 && 'Prenez 5 minutes pour relire le guide de l\'accompagnateur ci-dessus : ces règles protègent votre élève (et vous).'}
          </p>
          <button onClick={restart} data-testid="quiz-restart"
            className="mt-4 h-10 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
            <RotateCcw size={13} /> Refaire le quiz
          </button>
        </div>
      )}
    </div>
  );
}
